import PlaceIcon from '@mui/icons-material/Place';
import {
  Box,
  Button,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  LoadingIndicator,
  PRIVACY_POLICY_URL,
  TERMS_CONDITIONS_URL,
} from '@xolv-mybrightlink/common';
import { useRequestRegistrationCode } from './hooks/useRequestRegistrationCode';
import { SecuritySkeleton } from '../common/SecuritySkeleton';
import { RegisterErrorMessage } from './RegisterErrorMessage';
import { useRegister, useRegisterError } from './reducer/useRegister';

export const ZipCodeInput = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const registerError = useRegisterError();
  const registerState = useRegister();
  const cachedZipCode =
    registerState.data.type === 'REQUESTING_REGISTRATION_CODE'
      ? registerState.data.zipCode ?? ''
      : '';
  const [zipCode, setZipCode] = useState(cachedZipCode);
  const requestRegistrationCode = useRequestRegistrationCode();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const sanitizedInput = event.target.value.replace(/\D/g, '');
      setZipCode(sanitizedInput);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      await requestRegistrationCode(zipCode);
    },
    [zipCode, requestRegistrationCode],
  );

  return (
    <SecuritySkeleton onBackPressed={() => navigate(-1)}>
      <Box
        component='form'
        sx={{
          mt: 22,
        }}
        noValidate
        onSubmit={handleSubmit}
      >
        <Typography
          variant='body1'
          sx={{
            color: '#626a7a',
          }}
        >
          <FormattedMessage id='signup.button.label' />
        </Typography>
        <TextField
          margin='normal'
          required
          fullWidth
          id='zipInput'
          role={'zipInput'}
          name='zipInput'
          placeholder={intl.formatMessage({
            id: 'signup.zip.input.placeholder',
          })}
          inputProps={{
            'data-testid': 'zipInput',
            maxLength: 5,
          }}
          helperText={`${zipCode.length}/5`}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <PlaceIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
          variant='outlined'
          sx={{
            '& fieldset': {
              borderRadius: 3,
              borderColor: 'action.active',
            },
            '& input::placeholder': {
              fontSize: 14,
              opacity: 0.8,
              color: 'action.active',
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'right',
            },
          }}
          value={zipCode}
          onChange={handleChange}
          autoComplete='zip'
          autoFocus
        />
        <Button
          type='submit'
          fullWidth
          variant='contained'
          role={'signUpButton'}
          size='large'
          sx={{
            mt: 6,
            mb: 8,
            borderRadius: 30,
            py: 3.5,
          }}
          data-testid='signUpButton'
          disabled={zipCode.length == 0}
          disableElevation
        >
          <FormattedMessage id='signup.accept.button.label' />
        </Button>
        {!registerError ? (
          <Typography
            variant='body2'
            sx={{
              color: '#626a7a',
            }}
          >
            <FormattedMessage
              id='signup.footer.label1'
              values={{
                tusLink: (text) => (
                  <Link
                    rel='noopener'
                    href={TERMS_CONDITIONS_URL}
                    target='_blank'
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {text}
                  </Link>
                ),
                ppLink: (text) => (
                  <Link
                    rel='noopener'
                    href={PRIVACY_POLICY_URL}
                    target='_blank'
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {text}
                  </Link>
                ),
              }}
            />
            <br />
            <br />
            <FormattedMessage id='signup.footer.label2' />
          </Typography>
        ) : (
          <RegisterErrorMessage errorCode={registerError} />
        )}
      </Box>
      {registerState.loading && <LoadingIndicator fullHeight overlay />}
    </SecuritySkeleton>
  );
};
